import React, { useState } from 'react';
import { useLocalizedText } from '../../../hooks/localizedText';
import { useErrorMessages } from '../../../hooks/localization/errorMessages';
import { Container, Header } from '@amzn/awsui-components-react';
import { CoordinatorEventShiftsTable } from '../../myActivity/coordinatorEventShiftsTable';
import { useSuspensePaginatedRedVelvetQuery } from '../../../hooks/paginatedRedVelvetQuery';
import { DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE } from '../../myActivity/coordinatorEventShiftsTablePaginationPreference';
import { AccessibleSuspenseLoading } from '../../accessibility/accessibleSuspenseLoading';
import { COORDINATOR_EVENT_SHIFTS_TABLE } from '../../../utils/constants';

export interface EventDetailsProps {
  eventId: string;
  fromEventDetailPage?: boolean;
}

const NAMESPACES = ['translation'];

function SuspenseEventDetails(props: EventDetailsProps) {
  const { t } = useLocalizedText(NAMESPACES);
  const { resolveError } = useErrorMessages();
  const [shiftPageSize, setShiftPageSize] = useState(DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE);

  const { pageIndex, setPageIndex, queryResult } = useSuspensePaginatedRedVelvetQuery({
    redVelvetQueryKey: [
      'getEventDetailsByEventId',
      { eventId: props.eventId, pageSize: shiftPageSize },
    ],
  });
  if (queryResult.error) {
    return (
      <Container>
        <Header>{resolveError(queryResult.error)}</Header>
      </Container>
    );
  }

  if (!queryResult.data || !queryResult.data.detailedShifts) {
    return (
      <Container>
        <Header>{t('fullEvent.noShifts')}</Header>
      </Container>
    );
  }

  const resetEventDetails = (newPageSize: number) => {
    setPageIndex(1); // back to first page.
    setShiftPageSize(newPageSize);
  };

  const paginationProps = {
    loading: false,
    currentPage: pageIndex,
    totalItemsLength: queryResult.data?.detailedShifts?.length ?? 0,
    pageSize: shiftPageSize,
    lastEvaluatedKey: queryResult.data?.page,
    namespaces: NAMESPACES,
    onPageChange: setPageIndex,
    transactionKeys: {
      next: 'pagination.nextPageLabel',
      previous: 'pagination.previousPageLabel',
      label: 'pagination.paginationLabel',
    },
    tableName: COORDINATOR_EVENT_SHIFTS_TABLE,
  };

  const preferencesProps = {
    pageSize: shiftPageSize,
    resetDetailShifts: resetEventDetails,
  };

  return (
    <CoordinatorEventShiftsTable
      eventDetail={queryResult.data}
      urlFeatures={null}
      paginationProps={paginationProps}
      preferencesProps={preferencesProps}
      fromEventDetailPage={props.fromEventDetailPage ?? true}
    />
  );
}

export function EventDetailsTable(props: EventDetailsProps) {
  const { t } = useLocalizedText(NAMESPACES);

  return (
    <div>
      <AccessibleSuspenseLoading loadingText={t('fullEvent.loadingEvent')}>
        <SuspenseEventDetails
          eventId={props.eventId}
          fromEventDetailPage={props.fromEventDetailPage}
        />
      </AccessibleSuspenseLoading>
    </div>
  );
}
